import React from 'react';

// eslint-disable-next-line @atlaskit/platform/prefer-crypto-random-uuid -- Use crypto.randomUUID instead
import uuid from 'uuid/v4';

import type { PanelAttributes } from '@atlaskit/adf-schema/panel';
import { PanelType } from '@atlaskit/adf-schema/panel';
import { Emoji } from '@atlaskit/editor-common/emoji';
import { useSharedPluginStateWithSelector } from '@atlaskit/editor-common/hooks';
import type { NamedPluginStatesFromInjectionAPI } from '@atlaskit/editor-common/hooks';
import {
	PanelErrorIcon,
	PanelInfoIcon,
	PanelNoteIcon,
	PanelSuccessIcon,
	PanelWarningIcon,
} from '@atlaskit/editor-common/icons';
import { PanelSharedCssClassName } from '@atlaskit/editor-common/panel';
import type { PortalProviderAPI } from '@atlaskit/editor-common/portal';
import type { ProviderFactory } from '@atlaskit/editor-common/provider-factory';
import type {
	ExtractInjectionAPI,
	getPosHandler,
	getPosHandlerNode,
} from '@atlaskit/editor-common/types';
import type { Node as PMNode } from '@atlaskit/editor-prosemirror/model';
import { DOMSerializer } from '@atlaskit/editor-prosemirror/model';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';
import { akEditorCustomIconSize } from '@atlaskit/editor-shared-styles/consts';
import LightbulbIcon from '@atlaskit/icon/core/lightbulb';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';

import type { PanelPlugin, PanelPluginOptions } from '../panelPluginType';
import { panelAttrsToDom } from '../pm-plugins/utils/utils';
import { renderPanelIcon } from '../ui/renderPanelIcon';

/* eslint-disable @atlaskit/editor/no-re-export */
// Mapping export
export const panelIcons: {
	[key in PanelType]: React.ComponentType<{ label: string }>;
} = {
	info: PanelInfoIcon,
	success: PanelSuccessIcon,
	note: PanelNoteIcon,
	tip: LightbulbIcon,
	warning: PanelWarningIcon,
	error: PanelErrorIcon,
	custom: PanelInfoIcon,
};
/* eslint-enable @atlaskit/editor/no-re-export */

interface PanelIconAttributes {
	allowCustomPanel?: boolean;
	panelAttributes: PanelAttributes;
	pluginInjectionApi: ExtractInjectionAPI<PanelPlugin> | undefined;
	providerFactory?: ProviderFactory;
}

const selector = (
	states: NamedPluginStatesFromInjectionAPI<ExtractInjectionAPI<PanelPlugin>, 'emoji'>,
) => {
	return {
		emojiState: states.emojiState,
	};
};

export const PanelIcon = (props: PanelIconAttributes): React.JSX.Element | null => {
	const {
		allowCustomPanel,
		providerFactory,
		pluginInjectionApi,
		panelAttributes: { panelType, panelIcon, panelIconId, panelIconText },
	} = props;
	const { emojiState } = useSharedPluginStateWithSelector(pluginInjectionApi, ['emoji'], selector);
	const emojiProvider = emojiState?.emojiProvider;

	if (allowCustomPanel && panelIcon && panelType === PanelType.CUSTOM) {
		return (
			<Emoji
				emojiProvider={emojiProvider}
				providers={providerFactory}
				shortName={panelIcon}
				id={panelIconId}
				fallback={panelIconText}
				showTooltip={false}
				allowTextFallback={false}
				fitToHeight={akEditorCustomIconSize}
			/>
		);
	}

	const Icon = panelIcons[panelType];
	return Icon ? <Icon label={`${panelType} panel`} /> : null;
};

class PanelNodeView {
	node: PMNode;
	dom: HTMLElement;
	contentDOM: HTMLElement;
	icon: HTMLElement;
	getPos: getPosHandlerNode;
	view: EditorView;
	providerFactory?: ProviderFactory;
	nodeViewPortalProviderAPI: PortalProviderAPI;
	pluginOptions: PanelPluginOptions;
	key: string;

	constructor(
		node: PMNode,
		view: EditorView,
		getPos: getPosHandlerNode,
		pluginOptions: PanelPluginOptions,
		api: ExtractInjectionAPI<PanelPlugin> | undefined,
		nodeViewPortalProviderAPI: PortalProviderAPI,
		providerFactory?: ProviderFactory,
	) {
		this.nodeViewPortalProviderAPI = nodeViewPortalProviderAPI;
		this.providerFactory = providerFactory;
		this.pluginOptions = pluginOptions;
		this.view = view;
		this.node = node;
		// eslint-disable-next-line @atlaskit/platform/prefer-crypto-random-uuid -- Use crypto.randomUUID instead
		this.key = uuid();

		const { dom, contentDOM } = DOMSerializer.renderSpec(
			document,
			panelAttrsToDom(node.attrs as PanelAttributes, pluginOptions.allowCustomPanel || false),
		);
		this.getPos = getPos;
		// Ignored via go/ees005
		// eslint-disable-next-line @atlaskit/editor/no-as-casting
		this.dom = dom as HTMLElement;
		// Ignored via go/ees005
		// eslint-disable-next-line @atlaskit/editor/no-as-casting
		this.contentDOM = contentDOM as HTMLElement;
		// Ignored via go/ees005
		// eslint-disable-next-line @atlaskit/editor/no-as-casting
		this.icon = this.dom.querySelector(`.${PanelSharedCssClassName.icon}`) as HTMLElement;

		if (!this.icon) {
			return;
		}
		// set contentEditable as false to be able to select the custom panels with keyboard
		this.icon.contentEditable = 'false';

		const panelAttrs = node.attrs as PanelAttributes;

		// Determine if this is a standard panel type (info, note, success, warning, error)
		const isStandardPanel =
			panelAttrs.panelType &&
			[
				PanelType.INFO,
				PanelType.NOTE,
				PanelType.SUCCESS,
				PanelType.WARNING,
				PanelType.ERROR,
			].includes(panelAttrs.panelType);

		// For standard panels (info, note, success, warning, error), render icon directly as native DOM
		// This avoids Portal rendering delays that cause flickering on SSR and page transitions
		if (
			isStandardPanel &&
			expValEquals('platform_editor_vc90_transition_panel_icon', 'isEnabled', true)
		) {
			renderPanelIcon(panelAttrs.panelType, this.icon);
		} else {
			this.nodeViewPortalProviderAPI.render(
				() => (
					<PanelIcon
						pluginInjectionApi={api}
						allowCustomPanel={pluginOptions.allowCustomPanel}
						panelAttributes={panelAttrs}
						providerFactory={this.providerFactory}
					/>
				),
				this.icon,
				this.key,
			);
		}
	}

	ignoreMutation(mutation: MutationRecord | { target: Node; type: 'selection' }): boolean {
		// ignore mutation if it caused by the icon.
		if (!this.icon) {
			return false;
		}
		const isIcon = mutation.target === this.icon || mutation.target.parentNode === this.icon;
		// ignore mutation if it caused by the lazy load emoji inside icon.
		const isInsideIcon = this.icon.contains(mutation.target);
		return isIcon || isInsideIcon;
	}

	destroy(): void {
		const panelAttrs = this.node.attrs as PanelAttributes;
		// Determine if this is a standard panel type (info, note, success, warning, error)
		const isStandardPanel =
			panelAttrs.panelType &&
			[
				PanelType.INFO,
				PanelType.NOTE,
				PanelType.SUCCESS,
				PanelType.WARNING,
				PanelType.ERROR,
			].includes(panelAttrs.panelType);
		// Only remove Portal if it was used (for custom emoji panels)
		if (
			!(
				isStandardPanel &&
				expValEquals('platform_editor_vc90_transition_panel_icon', 'isEnabled', true)
			)
		) {
			this.nodeViewPortalProviderAPI.remove(this.key);
		}
	}
}

export const getPanelNodeView =
	(
		pluginOptions: PanelPluginOptions,
		api: ExtractInjectionAPI<PanelPlugin> | undefined,
		nodeViewPortalProviderAPI: PortalProviderAPI,
		providerFactory?: ProviderFactory,
	) =>
	(node: PMNode, view: EditorView, getPos: getPosHandler): PanelNodeView => {
		return new PanelNodeView(
			node,
			view,
			getPos as getPosHandlerNode,
			pluginOptions,
			api,
			nodeViewPortalProviderAPI,
			providerFactory,
		);
	};
